import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import { Box, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Button, { BUTTON_TYPE, BUTTON_VARIANT } from '../../../components/Button';
import { PRIMARY_COLOR } from '../../../theme/colorConstants';
import { isStartDateAndEndDateEqual } from '../helpers';

import Cadence from './FormSections/Cadence';
import Dates from './FormSections/Dates';
import LetsGetStarted from './FormSections/LetsGetStarted';
import Openings from './FormSections/Openings';
import Overstaff from './FormSections/Overstaff';
import ShiftTime from './FormSections/ShiftTime';
import ConfirmModal from './ConfirmModal';

const CreateJobFormBody = ({ handleSubmit, user, isProcessing }) => {
  const form = useForm();
  const formState = useFormState();
  const theme = useTheme();
  const intl = useIntl();

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

  const sectionWrapperSx = {
    marginBottom: theme.spacing(4),
  };

  // Modal opens when Review is clicked and form has no validation errors.
  const reviewButtonOnClick = () => {
    setHasClickedSubmit(true);
    const fieldsWithErrors = Object.keys(formState.errors);

    // Marks all as touched to display errors
    if (fieldsWithErrors.length > 0) {
      form.batch(() => {
        fieldsWithErrors.forEach((fieldName) => {
          form.blur(fieldName, true);
        });
      });
    } else {
      setIsConfirmOpen(true);
    }
  };

  const [activeStep, setActiveStep] = useState(0);
  const [oldFormLength, setOldFormLength] = useState(0);

  useEffect(() => {
    setHasClickedSubmit(false);
  }, [formState.values]);

  const sections = [
    {
      name: 'letsGetStarted',
      Section: LetsGetStarted,
    },
    {
      name: 'dates',
      Section: Dates,
    },
    !isStartDateAndEndDateEqual(formState.values) && {
      name: 'cadence',
      Section: Cadence,
    },
    {
      name: 'shiftTime',
      Section: ShiftTime,
    },
    {
      name: 'openings',
      Section: Openings,
    },
    user.role === UserRole.ADMIN && {
      name: 'overstaff',
      Section: Overstaff,
    },
  ].filter(Boolean);

  useEffect(() => {
    const fieldsWithErrors = Object.keys(formState.errors);
    const fieldsWithValues = Object.keys(formState.values);

    if (
      fieldsWithValues.length > oldFormLength &&
      fieldsWithErrors.length === 0 &&
      activeStep < sections?.length
    ) {
      setOldFormLength(fieldsWithValues.length);
      setActiveStep(activeStep + 1);
    }
  }, [activeStep, formState.errors, formState.values, oldFormLength, sections?.length]);

  return (
    <Grid sx={{ height: '100%', padding: theme.spacing(1) }}>
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            minHeight: '350px',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {sections.slice(0, activeStep + 1).map((sectionData) => (
            <sectionData.Section wrapperSx={sectionWrapperSx} user={user} key={sectionData?.name} />
          ))}
        </Box>
        <Grid container item justifyContent="flex-end" sx={{ width: '100%' }}>
          <Button
            data-testid="modalSubmitButton"
            variant={BUTTON_VARIANT.DEFAULT}
            type={BUTTON_TYPE.BUTTON}
            text={intl.formatMessage({ id: 'job.create.review' })}
            onClick={reviewButtonOnClick}
            actionColor={PRIMARY_COLOR[70]}
            disabled={hasClickedSubmit && Object.keys(formState.errors).length > 0}
          />
        </Grid>
        {isConfirmOpen && (
          <ConfirmModal
            isOpen={isConfirmOpen}
            onSubmit={handleSubmit}
            onClose={() => setIsConfirmOpen(false)}
            userRole={user?.role}
            isProcessing={isProcessing}
          />
        )}
      </form>
    </Grid>
  );
};

CreateJobFormBody.propTypes = {
  handleSubmit: PropTypes.func,
  isProcessing: PropTypes.bool,
  user: PropTypes.shape({
    role: PropTypes.string,
    employer: PropTypes.shape({
      corporation: PropTypes.string,
    }),
  }),
};

export default CreateJobFormBody;
